import React from 'react';
import { useObservableEagerState, useObservableState } from 'observable-hooks';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { lazyRefresh } from '@yabble/yabble-component-library/utils';
import { LoadingPage } from 'components/LoadingPage/LoadingPage';
import { PublicModuleTypes } from 'modules/PublicModule/PublicModule.types';
import { Lazy, moduleImports } from 'services/lazyModules';
import { routes } from 'services/routes';
import { getUserStore } from 'stores/user.store';

const ComplianceModule = lazyRefresh(moduleImports.compliance);
const PublicModule = lazyRefresh(moduleImports.public);

/**
 * Only ever our own captured `pathname`, but this is a redirect target read back out of
 * history state or OAuth `state`, so it is checked rather than trusted. A
 * protocol-relative `//host` (or `/\host`, which browsers read the same way) is a valid
 * *path* to the router and an off-site redirect to the browser.
 */
const isInternalRoute = (from: string | null | undefined): from is string =>
  !!from && from.startsWith('/') && !from.startsWith('//') && !from.startsWith('/\\');

/**
 * Once a session is established the browser is still sitting on a public path no
 * authenticated route matches — the sign-in callback, or `/login` for a restored session.
 * This sends the user on to whatever they originally asked for, and falls back to the
 * default route for anyone who came to `/login` directly.
 *
 * The deep link arrives two ways: through the IDP round trip as the store's `returnTo`,
 * or in history state, where `PublicModule` recorded it on the way in to `/login`.
 */
const AuthorizedOutlet: React.FC = () => {
  const { pathname, state } = useLocation();
  /** Eager: a first render with a placeholder `null` would already have navigated to the default route. */
  const returnTo = useObservableEagerState(getUserStore().$returnTo);

  if (pathname !== routes.authCallback && pathname !== routes.login) return <Outlet />;

  const from =
    pathname === routes.authCallback ? returnTo : (state as PublicModuleTypes.LocationState | null)?.from;

  return <Navigate to={isInternalRoute(from) ? from : routes.chat} replace />;
};

/**
 * The whole of the app's access control, as one switch on `$userState`. Its children only
 * render once the user is authorised, so no authenticated route mounts otherwise — and
 * `loading` renders a splash rather than the login page, so an already signed-in user
 * reloading never sees a flash of sign-in.
 */
const AuthGate: React.FC = () => {
  const userState = useObservableState(getUserStore().$userState, 'loading');

  if (userState === 'loading') return <LoadingPage />;
  if (userState === 'unauthorized')
    return (
      <Lazy>
        <PublicModule />
      </Lazy>
    );

  return <AuthorizedOutlet />;
};

const router = createBrowserRouter([
  {
    element: <AuthGate />,
    children: [
      {
        path: '*',
        element: (
          <Lazy>
            <ComplianceModule />
          </Lazy>
        ),
      },
    ],
  },
]);

const App: React.FC = () => <RouterProvider router={router} />;

export default App;
